import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory, useStandardConfirms, type FormModalMode } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type ActionMenuItem, type SyutGridColumn } from 'components/ui';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { getActiveDineOutletId } from 'auth/dineOutlet';
import { downloadCsv } from 'utils/csvExport';
import type { DineOutletAdmin, DineRequisition } from 'api/types';
import DineOpsRequisitionFormModal from './DineOpsRequisitionFormModal';
import DineOpsRequisitionViewModal from './DineOpsRequisitionViewModal';
import DineOpsRequisitionPurposesModal from './DineOpsRequisitionPurposesModal';
import { useRequisitionPurposes } from 'hooks/api/useDineOps';
import { purposeOptions, STATUS_BADGE, STATUS_LABEL, dateOnly, dateTime, kitchenProgress, money, requisitionActions } from './requisitionLabels';

/**
 * ============================================================================
 * REQUISITIONS (REQ-001, R1, 2026-10-22)
 * ============================================================================
 * Food and drink a department takes from an outlet's kitchen at transfer value:
 * welcome drinks for Front Office, staff meals, hospitality. Never billed, never
 * in the Bill Queue. A requisition is raised (any outlet, for a department or
 * cost centre), sent to the kitchen as NC KOTs (number, department and purpose under the header),
 * and issued to the department once the kitchen has it ready; the transfer
 * value is fixed then. Cancelled only before it reaches the kitchen.
 *
 * The list starts at the working outlet's open requisitions. Send and Issue run
 * in the working outlet, so they need it to be the requisition's own. The list
 * refreshes every 30 seconds while something is with the kitchen. The row menu
 * carries Send, Issue and Cancel too (design 5.1), under the same rules as the
 * view (requisitionActions); Issue and Cancel open the view on that step.
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

interface Filters { dineOutletId: string; status: string; purpose: string; from: string; to: string }

const STATUS_FILTER = [
  { value: 'OPEN', label: 'Open (requested, awaiting approval, approved or with the kitchen)' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'PENDING_APPROVAL', label: 'Awaiting approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'SENT', label: 'With the kitchen' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: '', label: 'All' },
];

const DineOpsRequisitionsScreen: React.FC<Props> = ({ tabId, moduleName = 'Requisitions', allowedActions = [] }) => {
  const canCreate = allowedActions.includes('create') || allowedActions.includes('add');
  const canIssue = allowedActions.includes('issue');
  const canCancel = allowedActions.includes('cancel');
  const canPurposes = allowedActions.includes('purposes');
  // Every purpose, inactive too: old requisitions are filtered by theirs.
  const { purposes, refetch: refetchPurposes } = useRequisitionPurposes(true);
  const [purposesOpen, setPurposesOpen] = useState(false);

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const { ConfirmDialogPortal } = useStandardConfirms();
  // The screen remounts on an outlet switch (an outlet-scoped module), so this is read once.
  const workingOutletId = useMemo(() => getActiveDineOutletId(), []);

  const [outlets, setOutlets] = useState<DineOutletAdmin[]>([]);
  useEffect(() => { dineOpsService.getOutlets(false).then(setOutlets).catch(() => setOutlets([])); }, []);

  // ---- filters, search and the server-side list ---------------------------------------------
  const initial: Filters = { dineOutletId: workingOutletId, status: 'OPEN', purpose: '', from: '', to: '' };
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>(initial);
  const [draft, setDraft] = useState<Filters>(initial);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300); return () => clearTimeout(t); }, [searchTerm]);

  const [rows, setRows] = useState<DineRequisition[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const loadSeq = useRef(0);
  const load = useCallback(async (quiet = false) => {
    const seq = ++loadSeq.current;   // a reply to an older request never overwrites a newer one
    if (!quiet) setLoading(true);
    try {
      const res = await dineOpsService.getRequisitions({
        page, pageSize, searchTerm: debouncedSearch || undefined, dineOutletId: filters.dineOutletId || undefined,
        status: filters.status || undefined, purpose: filters.purpose || undefined,
        from: filters.from || undefined, to: filters.to || undefined,
      });
      if (seq !== loadSeq.current) return;
      setRows(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      if (seq === loadSeq.current && !quiet) notificationService.toast.error(err?.response?.data?.message || err?.message || 'The requisitions could not be loaded.');
    } finally {
      if (seq === loadSeq.current) { setLoading(false); setReady(true); }
    }
  }, [page, pageSize, debouncedSearch, filters]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [debouncedSearch, filters]);

  // The kitchen moves on its own: while something is with it, look again every 30 seconds.
  const withKitchen = rows.some((r) => r.requisitionStatus === 'SENT');
  useEffect(() => {
    if (!withKitchen) return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible') void load(true); }, 30000);
    return () => clearInterval(t);
  }, [withKitchen, load]);

  const appliedCount = (filters.dineOutletId ? 1 : 0) + (filters.status ? 1 : 0) + (filters.purpose ? 1 : 0) + (filters.from || filters.to ? 1 : 0);

  // ---- form, view and actions ----------------------------------------------------------------
  const [editor, setEditor] = useState<{ mode: FormModalMode; record: DineRequisition | null } | null>(null);
  const [viewing, setViewing] = useState<DineRequisition | null>(null);
  /** The step the view opens on when it is opened from the row menu's Issue…, Cancel…, Approve… or Reject…. */
  const [viewAction, setViewAction] = useState<'issue' | 'cancel' | 'approve' | 'reject' | null>(null);
  const [busy, setBusy] = useState(false);
  const [createInstanceId, setCreateInstanceId] = useState('1');

  const restoreIfMinimized = useCallback((modalId: string | null): boolean => {
    if (!modalId || !isMinimized(modalId)) return false;
    const modal = getMinimizedModal(modalId);
    dismissMinimized(modalId);
    modal?.onRestore?.();
    return true;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  /** Edit, copy and view need the lines: always read it fresh. */
  const loadFull = useCallback(async (id: string): Promise<DineRequisition | null> => {
    try { return await dineOpsService.getRequisition(id); } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'The requisition could not be loaded. Refresh and try again.');
      return null;
    }
  }, []);

  const openCreate = useCallback(() => {
    let next = 1;
    while (tabId && isMinimized(`${tabId}-form-modal-create-${next}`)) next += 1;
    setCreateInstanceId(String(next));
    setEditor({ mode: 'create', record: null });
  }, [tabId, isMinimized]);

  const openFor = useCallback(async (mode: FormModalMode, row: DineRequisition) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-${mode}-${row.id}` : null)) return;
    const full = await loadFull(row.id);
    if (full) { setViewing(null); setEditor({ mode, record: full }); }
  }, [tabId, restoreIfMinimized, loadFull]);

  const openView = useCallback(async (row: DineRequisition, step: 'issue' | 'cancel' | 'approve' | 'reject' | null = null) => {
    if (!step && restoreIfMinimized(tabId ? `${tabId}-view-modal-${row.id}` : null)) return;
    const full = await loadFull(row.id);
    if (full) { setViewAction(step); setViewing(full); }
  }, [tabId, restoreIfMinimized, loadFull]);

  const closeView = useCallback(() => { setViewing(null); setViewAction(null); }, []);

  const handleRestoreModal = useCallback(async (mode: FormModalMode, instanceId?: string, recordId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    if (mode === 'create' || !recordId) { setEditor({ mode, record: null }); return; }
    const full = await loadFull(recordId);
    if (full) setEditor({ mode, record: full });
  }, [loadFull]);

  const handleRestoreView = useCallback(async (recordId: string) => {
    const full = await loadFull(recordId);
    if (full) setViewing(full);
  }, [loadFull]);

  /** Runs one action; an open view shows the result (a row-menu Send opens none), the list reloads. */
  const act = useCallback(async (work: () => Promise<{ requisition: DineRequisition; message?: string }>, success: string) => {
    setBusy(true);
    setViewAction(null);
    try {
      const { requisition, message } = await work();
      const full = await loadFull(requisition.id);
      if (full) setViewing((v) => (v && v.id === full.id ? full : v));
      if (message && message !== success && !/successfully|sent to the kitchen/i.test(message)) notificationService.toast.warning(message);
      else notificationService.toast.success(success);
      void load(true);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'That could not be done.');
      if (viewing) { const full = await loadFull(viewing.id); if (full) setViewing(full); }
    } finally {
      setBusy(false);
    }
  }, [loadFull, load, viewing]);

  const send = useCallback((r: DineRequisition) => act(
    () => dineOpsService.sendRequisition(r.id), `${r.requisitionNumber} sent to the kitchen.`), [act]);
  const issue = useCallback((r: DineRequisition, receivedBy: string) => act(
    async () => ({ requisition: await dineOpsService.issueRequisition(r.id, receivedBy, r.rowVersion) }),
    `${r.requisitionNumber} issued to ${r.requestingUnitName}.`), [act]);
  const cancel = useCallback((r: DineRequisition, reason: string) => act(
    async () => ({ requisition: await dineOpsService.cancelRequisition(r.id, reason, r.rowVersion) }),
    `${r.requisitionNumber} cancelled.`), [act]);
  const approve = useCallback((r: DineRequisition, note: string | undefined) => act(
    async () => ({ requisition: await dineOpsService.approveRequisition(r.id, note, r.rowVersion) }),
    `${r.requisitionNumber} approved. It can be sent to the kitchen.`), [act]);
  const reject = useCallback((r: DineRequisition, reason: string) => act(
    async () => ({ requisition: await dineOpsService.rejectRequisition(r.id, reason, r.rowVersion) }),
    `${r.requisitionNumber} rejected.`), [act]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) { notificationService.toast.warning('No data to export'); return; }
    downloadCsv(`requisitions_${new Date().toISOString().split('T')[0]}.csv`,
      ['Number', 'Date', 'Outlet', 'Department', 'Purpose', 'Dishes', 'Needed at', 'Status', 'Value', 'Received by'],
      rows.map((r) => [r.requisitionNumber, dateOnly(r.businessDate), r.outletName, r.requestingUnitName, r.purposeName ?? r.purpose,
        r.itemSummary ?? '', dateTime(r.neededAt), STATUS_LABEL[r.requisitionStatus],
        money(r.requisitionStatus === 'ISSUED' ? r.transferTotal : r.estimatedTotal), r.receivedByName ?? '']));
  }, [rows]);

  // ---- grid ---------------------------------------------------------------------------------
  const columns = useMemo<SyutGridColumn<DineRequisition>[]>(() => [
    { id: 'requisitionNumber', header: 'Number', accessorKey: 'requisitionNumber', size: 190,
      cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.requisitionNumber}</span> },
    { id: 'requestingUnitName', header: 'Department', accessorKey: 'requestingUnitName', size: 170 },
    { id: 'purpose', header: 'Purpose', accessorKey: 'purpose', size: 120, cell: ({ row }) => row.original.purposeName ?? row.original.purpose },
    { id: 'itemSummary', header: 'Dishes', accessorKey: 'itemSummary', size: 280,
      cell: ({ row }) => <span title={row.original.itemSummary ?? ''}>{row.original.itemSummary}</span> },
    { id: 'neededAt', header: 'Needed at', accessorKey: 'neededAt', size: 150, cell: ({ row }) => dateTime(row.original.neededAt) },
    { id: 'outletName', header: 'Outlet', accessorKey: 'outletName', size: 140 },
    { id: 'status', header: 'Status', accessorKey: 'requisitionStatus', size: 190,
      cell: ({ row }) => {
        const r = row.original;
        const progress = kitchenProgress(r);
        return (
          <span>
            <span className={`badge ${STATUS_BADGE[r.requisitionStatus]}`}>{STATUS_LABEL[r.requisitionStatus]}</span>
            {progress && <span style={{ marginLeft: 6, fontSize: '0.75rem', color: progress === 'Ready to issue' ? 'var(--success)' : 'var(--text-muted)' }}>{progress}</span>}
          </span>
        );
      } },
    { id: 'value', header: 'Value', accessorKey: 'estimatedTotal', size: 100,
      cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.original.requisitionStatus === 'ISSUED' ? row.original.transferTotal : row.original.estimatedTotal)}</span> },
    { id: 'businessDate', header: 'Raised', accessorKey: 'businessDate', size: 110, cell: ({ row }) => dateOnly(row.original.businessDate) },
  ], []);

  const getRowActions = useCallback((record: DineRequisition): ActionMenuItem[] => {
    const a = requisitionActions(record, workingOutletId, { canEdit: canCreate, canIssue, canCancel });
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => { void openView(record); } },
    ];
    if (a.mayEdit) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => { void openFor('edit', record); },
        description: record.requisitionStatus === 'APPROVED' ? 'A change asks for approval again.' : undefined });
    }
    if (a.mayDecide) {
      items.push({ id: 'approve', label: 'Approve…', icon: <IconSystem.checkCircle size={18} />, disabled: busy, onClick: () => { void openView(record, 'approve'); },
        description: `${money(record.estimatedTotal)} at transfer value; then it can go to the kitchen.` });
      items.push({ id: 'reject', label: 'Reject…', icon: <IconSystem.xCircle size={18} />, variant: 'danger', disabled: busy, onClick: () => { void openView(record, 'reject'); },
        description: 'Final; asks why.' });
    } else if (a.awaiting) {
      items.push({ id: 'awaiting', label: 'Awaiting approval', icon: <IconSystem.clock size={18} />, disabled: true, onClick: () => undefined, description: a.awaiting });
    }
    // An action that applies but cannot run here or yet stays in the menu, disabled, with the reason.
    if (a.maySend) {
      items.push({ id: 'send', label: record.requisitionStatus === 'SENT' ? 'Send again' : 'Send to kitchen', icon: <IconSystem.arrowRight size={18} />,
        disabled: busy || !!a.notHere, onClick: () => { void send(record); },
        description: a.notHere ?? (record.requisitionStatus === 'SENT' ? `${a.held} dish(es) waiting for a closed station.` : 'Raises NC KOTs for the kitchen.') });
    }
    if (a.mayIssue) {
      items.push({ id: 'issue', label: 'Issue…', icon: <IconSystem.check size={18} />,
        disabled: busy || !!a.notHere || !a.ready, onClick: () => { void openView(record, 'issue'); },
        description: a.notHere ?? a.notReady ?? 'Hand it to the department; asks who received it.' });
    }
    if (canCreate) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => { void openFor('copy', record); },
        description: 'A new requisition with the same department, purpose and dishes.' });
    }
    if (a.mayCancel) {
      items.push({ id: 'cancel', label: 'Cancel requisition…', icon: <IconSystem.x size={18} />, variant: 'danger',
        disabled: busy, onClick: () => { void openView(record, 'cancel'); } });
    }
    return items;
  }, [workingOutletId, canCreate, canIssue, canCancel, busy, openFor, openView, send]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(openCreate, 'Raise requisition'), visible: canCreate },
    actionButtonFactory.refresh(() => { void load(); }),
    actionButtonFactory.export_(handleExport, 'Export to CSV'),
    { id: 'purposes', icon: <IconSystem.settings size={18} />, label: '', onClick: () => setPurposesOpen(true),
      tooltip: 'Purposes: the reasons a requisition can be raised for', variant: 'icon', visible: canPurposes },
  ];

  const outletOptions = outlets.map((o) => ({ value: o.id, label: `${o.outletName} (${o.outletCode})` }));
  const sameFilters = JSON.stringify(draft) === JSON.stringify(filters);

  return (
    <EnterpriseScreenShell
      title="Requisitions"
      description="Food and drink departments take from an outlet's kitchen at transfer value; never billed"
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search number, department or dish' }}
      filterConfig={{ onClick: () => setFilterOpen((o) => !o), isActive: filterOpen, appliedCount, tooltip: 'Filters' }}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Outlet" options={outletOptions} value={draft.dineOutletId} isClearable size="sm" placeholder="Every outlet"
                onChange={(v) => setDraft((d) => ({ ...d, dineOutletId: (v as string) || '' }))} helperText="Starts at the working outlet." />
            </div>
            <div className="filter-section">
              <SyutSelect label="Status" options={STATUS_FILTER} value={draft.status} size="sm" placeholder="All"
                onChange={(v) => setDraft((d) => ({ ...d, status: (v as string) || '' }))} />
            </div>
            <div className="filter-section">
              <SyutSelect label="Purpose" options={purposeOptions(purposes)} value={draft.purpose} isClearable size="sm" placeholder="Any purpose"
                onChange={(v) => setDraft((d) => ({ ...d, purpose: (v as string) || '' }))} />
            </div>
            <div className="filter-section">
              <label className="form-label">Raised from</label>
              <input type="date" className="form-input" value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
            </div>
            <div className="filter-section">
              <label className="form-label">Raised to</label>
              <input type="date" className="form-input" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { const cleared: Filters = { dineOutletId: '', status: '', purpose: '', from: '', to: '' }; setDraft(cleared); setFilters(cleared); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilters(draft)} disabled={sameFilters}>Apply</button>
          </div>
        </div>
      }
    >
      <div className="data-grid-container">
        {!ready ? (
          <GridLoadingSkeleton rows={8} columns={8} />
        ) : (
          <SyutGrid
            data={rows}
            columns={columns}
            enablePagination
            manualPagination
            totalCount={totalCount}
            currentPage={page - 1}
            pageSize={pageSize}
            onPageChange={(pageIndex) => setPage(pageIndex + 1)}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            isLoading={loading}
            frozenRightColumns={['actions']}
            rowActions={getRowActions}
            emptyMessage={canCreate ? 'No requisitions here. Use + to raise one: welcome drinks, a staff meal, hospitality.' : 'No requisitions match.'}
          />
        )}
      </div>

      {editor && (
        <DineOpsRequisitionFormModal
          isOpen
          mode={editor.mode}
          record={editor.record}
          outlets={outlets}
          defaultOutletId={workingOutletId || undefined}
          tabId={tabId}
          moduleName={moduleName}
          createInstanceId={createInstanceId}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); void load(); }}
          onRestoreModal={handleRestoreModal}
        />
      )}

      <DineOpsRequisitionPurposesModal
        isOpen={purposesOpen}
        onClose={() => setPurposesOpen(false)}
        onChanged={() => { void refetchPurposes(); void load(); }}
      />

      <DineOpsRequisitionViewModal
        isOpen={!!viewing}
        record={viewing}
        workingOutletId={workingOutletId}
        canEdit={canCreate}
        canIssue={canIssue}
        canCancel={canCancel}
        busy={busy}
        initialAction={viewAction}
        tabId={tabId}
        moduleName={moduleName}
        onClose={closeView}
        onEdit={(r) => { void openFor('edit', r); }}
        onSend={(r) => { void send(r); }}
        onIssue={(r, by) => { void issue(r, by); }}
        onCancel={(r, why) => { void cancel(r, why); }}
        onApprove={(r, note) => { void approve(r, note); }}
        onReject={(r, why) => { void reject(r, why); }}
        onRestoreView={(id) => { void handleRestoreView(id); }}
      />
    </EnterpriseScreenShell>
  );
};

export default DineOpsRequisitionsScreen;
